import { AppIcon } from './ui/icon'
import { mdiCalendar, mdiCodeBraces, mdiFormatListBulleted, mdiFormatText, mdiPound, mdiToggleSwitchOffOutline } from '@mdi/js'
import { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Checkbox } from './ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Label } from './ui/label'
import { Separator } from './ui/separator'
import { Textarea } from './ui/textarea'
import { MultilingualInput } from './ui/multilingual-input'
import { MultilingualFormProvider } from './ui/multilingual-form-context'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from './ui/form'
import { useToast } from '../hooks/use-toast'
import { SelectValuesManager } from './SelectValuesManager'
import {
  usePropertyDefinition,
  usePropertyDefinitions,
  useUpdatePropertyDefinition,
  usePropertyCategories,
  type UpdatePropertyDefinitionRequest,
  type PropertyCategory,
} from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { PropertyDerivations } from './derivations/PropertyDerivations'
import { localizedPatchValue, toBilingualForm } from '../lib/localization'

const DATA_TYPES = [
  { value: 'text', icon: mdiFormatText },
  { value: 'number', icon: mdiPound },
  { value: 'boolean', icon: mdiToggleSwitchOffOutline },
  { value: 'date', icon: mdiCalendar },
  { value: 'enum', icon: mdiFormatListBulleted },
  { value: 'json', icon: mdiCodeBraces },
] as const

const ENTITY_TYPE_VALUES = ['asset', 'document', 'user'] as const

const formSchema = z.object({
  name: z.object({
    en: z.string().min(1, 'English name is required').max(100, 'English name must be less than 100 characters'),
    fr: z.string().max(100, 'French name must be less than 100 characters').default(''),
  }),
  description: z.object({
    en: z.string().max(500, 'English description must be less than 500 characters').default(''),
    fr: z.string().max(500, 'French description must be less than 500 characters').default(''),
  }).default({ en: '', fr: '' }),
  code: z.string().min(1, 'Code is required').max(50, 'Code must be less than 50 characters'),
  data_type: z.enum(['text', 'number', 'boolean', 'date', 'enum', 'json']),
  target_entity_types: z.array(z.enum(['asset', 'document', 'user'])).min(1, 'At least one target entity type must be selected'),
  enabled: z.boolean().default(true),
  with_time: z.boolean().default(false),
  regex_pattern: z.string().default(''),
  link_pattern: z.string().default(''),
  min_value: z.string().default(''),
  max_value: z.string().default(''),
  integer_only: z.boolean().default(false),
  semantic: z.enum(['neutral', 'negative', 'positive', 'personal', 'warning']).default('neutral'),
  json_schema: z.string().default(''),
  multiple: z.boolean().default(false),
  attribute: z.boolean().default(false),
  derived: z.boolean().default(false),
  system_managed: z.boolean().default(false),
  hidden: z.boolean().default(false),
  suggestions_enabled: z.boolean().default(false),
  suggestions_scope: z.enum(['asset_type', 'organization']).default('asset_type'),
  suggestions_depends_on: z.array(z.string()).default([]),
})

type FormData = z.infer<typeof formSchema>

interface PropertyEditPageProps {
  defaultValues?: Partial<FormData>
}

export function PropertyEditPage({ defaultValues }: PropertyEditPageProps = {}) {
  const { org, propertyUid } = useParams({ strict: false })
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation('properties')

  const { data: property, isLoading, error } = usePropertyDefinition(org || 'demo', propertyUid || '')
  const updateProperty = useUpdatePropertyDefinition(org || 'demo', propertyUid || '')
  const { data: categories = [] } = usePropertyCategories(org || 'demo')
  const { data: allProperties = [] } = usePropertyDefinitions(org || 'demo')

  // Track category_uid separately since it's not in the Zod form schema
  const [selectedCategoryUid, setSelectedCategoryUid] = useState<string | null>(null)
  const [initialCategoryUid, setInitialCategoryUid] = useState<string | null>(null)

  const form = useForm<FormData>({
    defaultValues: {
      name: { en: '', fr: '' },
      description: { en: '', fr: '' },
      code: '',
      data_type: 'text' as const,
      target_entity_types: [],
      enabled: true,
      with_time: false,
      regex_pattern: '',
      link_pattern: '',
      min_value: '',
      max_value: '',
      integer_only: false,
      semantic: 'neutral' as const,
      json_schema: '',
      multiple: false,
      attribute: false,
      derived: false,
      system_managed: false,
      hidden: false,
      suggestions_enabled: false,
      suggestions_scope: 'asset_type' as const,
      suggestions_depends_on: [],
      ...defaultValues,
    },
    mode: 'onChange',
  })

  // Helper function to handle multilingual property names
  const getPropertyName = (property: any) => {
    if (!property?.name) return property?.code || 'Loading...'

    // Handle multilingual names
    if (typeof property.name === 'object' && property.name !== null) {
      const names = property.name as Record<string, string>
      return names.en || names.fr || Object.values(names)[0] || property.code || 'Loading...'
    }

    return property.name || property.code || 'Loading...'
  }

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('mapping_rules.breadcrumb_home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/properties` },
    { title: getPropertyName(property), path: `/${org}/properties/${propertyUid}` },
    { title: t('edit.breadcrumb_edit') }
  ], [org, propertyUid, property, t])

  useBreadcrumbs(breadcrumbs)

  // Initialize form data when property loads
  useEffect(() => {
    if (property) {
      // Handle multilingual property names from API
      let nameValue = { en: '', fr: '' }
      if (property.name) {
        if (typeof property.name === 'object') {
          nameValue = { en: property.name.en || '', fr: property.name.fr || '' }
        } else {
          // Fallback for legacy string names - put in English field
          nameValue = { en: property.name, fr: '' }
        }
      }

      // Multilingual description (legacy plain string lands in English)
      const descriptionValue = toBilingualForm(property.description)

      const formValues: FormData = {
        name: nameValue,
        description: descriptionValue,
        code: property.code || '',
        data_type: property.data_type as FormData['data_type'],
        target_entity_types: property.target_entity_types || [],
        enabled: property.enabled ?? true,
        with_time: property.properties?.with_time === true,
        regex_pattern: (typeof property.properties?.regex_pattern === 'string' ? property.properties.regex_pattern : ''),
        link_pattern: (typeof property.properties?.link_pattern === 'string' ? property.properties.link_pattern : ''),
        min_value: property.properties?.min_value?.toString() || '',
        max_value: property.properties?.max_value?.toString() || '',
        integer_only: property.properties?.integer_only === true,
        semantic: (property.properties?.semantic as FormData['semantic']) || 'neutral',
        json_schema: (typeof property.properties?.json_schema === 'string' ? property.properties.json_schema : ''),
        multiple: property.properties?.multiple === true,
        attribute: property.flags?.includes('attribute') ?? false,
        derived: property.flags?.includes('derived') ?? false,
        system_managed: property.flags?.includes('system_managed') ?? false,
        hidden: property.flags?.includes('hidden') ?? false,
        suggestions_enabled: Boolean(property.properties?.suggestions),
        suggestions_scope: ((property.properties?.suggestions as { scope?: string } | undefined)?.scope === 'organization'
          ? 'organization'
          : 'asset_type') as FormData['suggestions_scope'],
        suggestions_depends_on: ((property.properties?.suggestions as { depends_on?: string[] } | undefined)?.depends_on ?? []),
        // Allow defaultValues to override if provided
        ...defaultValues,
      }

      form.reset(formValues)

      // Sync category state
      const catUid = (property as any).property_category_uid || null
      setSelectedCategoryUid(catUid)
      setInitialCategoryUid(catUid)

      // Force re-render by setting values explicitly as a backup
      // DO NOT REMOVE THIS CODE, it's a workaround for a bug in the form management
      // that causes the data_type to be undefined when the property is loaded
      setTimeout(() => {
        form.setValue('data_type', property.data_type)
      }, 100)
    }
  }, [property, defaultValues, form])

  // Candidates for `suggestions.depends_on`: the organization's enabled
  // text/enum asset properties, minus the property being edited.
  const dependencyCandidates = useMemo(() => {
    return allProperties
      .filter(p => (p.data_type === 'text' || p.data_type === 'enum')
        && (p.target_entity_types || []).includes('asset')
        && p.enabled !== false
        && p.code !== property?.code)
      .map(p => ({
        code: p.code || '',
        label: (typeof p.name === 'object' && p.name !== null
          ? (p.name as Record<string, string>).en || (p.name as Record<string, string>).fr || p.code
          : p.code) || '',
      }))
      .filter(p => p.code)
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [allProperties, property?.code])

  const formData = form.watch()

  const isFormValid = form.formState.isValid

  const hasChanges = useMemo(() => {
    if (!property) return false

    // Compare multilingual names
    let originalName = { en: '', fr: '' }
    if (property.name) {
      if (typeof property.name === 'object') {
        originalName = { en: property.name.en || '', fr: property.name.fr || '' }
      } else {
        originalName = { en: property.name, fr: '' }
      }
    }

    // Compare multilingual descriptions
    const originalDescription = toBilingualForm(property.description)

    return (
      JSON.stringify(formData.name) !== JSON.stringify(originalName) ||
      JSON.stringify(formData.description) !== JSON.stringify(originalDescription) ||
      formData.code !== property.code ||
      formData.data_type !== property.data_type ||
      JSON.stringify([...formData.target_entity_types].sort()) !==
      JSON.stringify([...(property.target_entity_types || [])].sort()) ||
      formData.enabled !== (property.enabled ?? true) ||
      formData.with_time !== (property.properties?.with_time === true) ||
      formData.regex_pattern !== (typeof property.properties?.regex_pattern === 'string' ? property.properties.regex_pattern : '') ||
      formData.link_pattern !== (typeof property.properties?.link_pattern === 'string' ? property.properties.link_pattern : '') ||
      formData.min_value !== (property.properties?.min_value?.toString() || '') ||
      formData.max_value !== (property.properties?.max_value?.toString() || '') ||
      formData.integer_only !== (property.properties?.integer_only === true) ||
      formData.semantic !== ((property.properties?.semantic as FormData['semantic']) || 'neutral') ||
      formData.json_schema !== (typeof property.properties?.json_schema === 'string' ? property.properties.json_schema : '') ||
      formData.multiple !== (property.properties?.multiple === true) ||
      formData.attribute !== (property.flags?.includes('attribute') ?? false) ||
      formData.derived !== (property.flags?.includes('derived') ?? false) ||
      formData.system_managed !== (property.flags?.includes('system_managed') ?? false) ||
      formData.hidden !== (property.flags?.includes('hidden') ?? false) ||
      formData.suggestions_enabled !== Boolean(property.properties?.suggestions) ||
      formData.suggestions_scope !== ((property.properties?.suggestions as { scope?: string } | undefined)?.scope === 'organization' ? 'organization' : 'asset_type') ||
      JSON.stringify([...formData.suggestions_depends_on].sort()) !==
      JSON.stringify([...((property.properties?.suggestions as { depends_on?: string[] } | undefined)?.depends_on ?? [])].sort()) ||
      selectedCategoryUid !== initialCategoryUid
    )
  }, [formData, property, selectedCategoryUid, initialCategoryUid])

  const onSubmit = async (data: any) => {
    // Manual validation using Zod
    try {
      const validatedData = formSchema.parse(data)

      if (!hasChanges) {
        return
      }

      const propertyData: UpdatePropertyDefinitionRequest = {
        name: validatedData.name,
        code: validatedData.code,
        data_type: validatedData.data_type as UpdatePropertyDefinitionRequest['data_type'],
        target_entity_types: validatedData.target_entity_types as UpdatePropertyDefinitionRequest['target_entity_types'],
        enabled: validatedData.enabled,
      }

      // Absent = unchanged; `{}` clears it once every language was emptied.
      const description = localizedPatchValue(validatedData.description, property?.description)
      if (description !== undefined) {
        propertyData.description = description
      }

      // Include properties field based on data type
      const properties: Record<string, any> = {}
      
      if (validatedData.data_type === 'date') {
        properties.with_time = validatedData.with_time
      }
      
      if (validatedData.data_type === 'text') {
        if (validatedData.regex_pattern.trim()) {
          properties.regex_pattern = validatedData.regex_pattern.trim()
        }
        if (validatedData.link_pattern.trim()) {
          properties.link_pattern = validatedData.link_pattern.trim()
        }
        // Value suggestions (spec 2026-09-02-03). Absent key = no suggestions:
        // serial numbers and free comments must stay bare inputs.
        if (validatedData.suggestions_enabled) {
          const suggestions: Record<string, unknown> = { scope: validatedData.suggestions_scope }
          if (validatedData.suggestions_depends_on.length > 0) {
            suggestions.depends_on = validatedData.suggestions_depends_on
          }
          properties.suggestions = suggestions
        }
      }
      
      if (validatedData.data_type === 'number') {
        if (validatedData.min_value.trim()) {
          const minVal = parseFloat(validatedData.min_value)
          if (!isNaN(minVal)) {
            properties.min_value = minVal
          }
        }
        
        if (validatedData.max_value.trim()) {
          const maxVal = parseFloat(validatedData.max_value)
          if (!isNaN(maxVal)) {
            properties.max_value = maxVal
          }
        }
        
        if (validatedData.integer_only) {
          properties.integer_only = true
        }
      }
      
      if (validatedData.data_type === 'boolean') {
        if (validatedData.semantic !== 'neutral') {
          properties.semantic = validatedData.semantic
        }
      }

      if (validatedData.data_type === 'json') {
        if (validatedData.json_schema.trim()) {
          properties.json_schema = validatedData.json_schema.trim()
        }
      }

      if (validatedData.data_type === 'enum') {
        if (validatedData.multiple) {
          properties.multiple = true
        }
      }

      if (Object.keys(properties).length > 0) {
        propertyData.properties = properties
      }

      // Build flags array
      const flags: string[] = []
      if (validatedData.attribute && (validatedData.data_type === 'boolean' || validatedData.data_type === 'enum')) {
        flags.push('attribute')
      }
      if (validatedData.derived) {
        flags.push('derived')
      }
      if (validatedData.system_managed) {
        flags.push('system_managed')
      }
      if (validatedData.hidden) {
        flags.push('hidden')
      }
      ;(propertyData as any).flags = flags

      // Include property_category_uid (null to remove)
      ;(propertyData as any).property_category_uid = selectedCategoryUid || null

      await updateProperty.mutateAsync(propertyData)

      navigate({ to: `/${org}/properties/${propertyUid}` })
    } catch (validationError) {
      if (validationError instanceof z.ZodError) {
        validationError.issues.forEach((err: any) => {
          toast({
            title: t('common:status.error'),
            description: err.message,
            variant: "destructive",
          })
        })
      } else {
        console.error('Error updating property:', validationError)
        toast({
          title: t('common:status.error'),
          description: t('edit.update_error'),
          variant: "destructive",
        })
      }
    }
  }

  const handleCancel = () => {
    navigate({ to: `/${org}/properties/${propertyUid}` })
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="bg-card rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-center">
            <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
            <span className="text-muted-foreground">{t('edit.loading')}</span>
          </div>
        </div>
      </div>
    )
  }

  if (error || !property) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="bg-card rounded-lg shadow-lg p-6">
          <div className="flex flex-col items-center justify-center">
            <p className="text-destructive mb-2">{t('edit.error')}</p>
            <p className="text-sm text-muted-foreground mb-4">
              {(error as any)?.title || (error as any)?.message || t('edit.not_found')}
            </p>
            <Button onClick={() => navigate({ to: `/${org}/properties` })}>
              {t('edit.back')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.properties}
            title={t('edit.title')}
            description={t('edit.subtitle')}
          />
        </div>

        <MultilingualFormProvider>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit as any)} className="p-6 space-y-6">
              {/* Name Field */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('edit.name_label')} <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <MultilingualInput
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={t('edit.name_placeholder')}
                        data-testid="property-name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Description Field */}
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('edit.description_label')}</FormLabel>
                    <FormControl>
                      <MultilingualInput
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={t('edit.description_placeholder')}
                        data-testid="property-description"
                      />
                    </FormControl>
                    <FormDescription>
                      {t('edit.description_hint')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Code Field */}
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('edit.code_label')} <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t('edit.code_placeholder')}
                        className="w-full font-mono"
                        data-testid="property-code"
                      />
                    </FormControl>
                    <FormDescription>
                      {t('edit.code_hint')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Category Field */}
              <div className="space-y-2">
                <Label>{t('edit.category_label')}</Label>
                <Select
                  value={selectedCategoryUid || 'none'}
                  onValueChange={(value) => setSelectedCategoryUid(value === 'none' ? null : value)}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder={t('edit.category_placeholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('edit.category_none')}</SelectItem>
                    {categories.map((category: PropertyCategory) => {
                      const name = typeof category.name === 'object' && category.name !== null
                        ? (category.name as Record<string, string>).en || (category.name as Record<string, string>).fr || Object.values(category.name as Record<string, string>)[0] || category.code
                        : String(category.name) || category.code
                      return (
                        <SelectItem key={category.uid} value={category.uid!}>
                          {name}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {t('edit.category_hint')}
                </p>
              </div>

              {/* Data Type Field */}
              <FormField
                control={form.control}
                name="data_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('edit.data_type_label')} <span className="text-destructive">*</span>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="property-data-type">
                          <SelectValue placeholder={t('edit.data_type_placeholder')}>
                            {field.value && (() => {
                              const selectedType = DATA_TYPES.find(type => type.value === field.value)
                              if (selectedType) {
                                return (
                                  <div className="flex items-center space-x-2">
                                    <AppIcon path={selectedType.icon} size="sm" />
                                    <span>{t(`data_types.${selectedType.value}`)}</span>
                                  </div>
                                )
                              }
                              return null
                            })()}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DATA_TYPES.map((type) => {
                          return (
                            <SelectItem key={type.value} value={type.value}>
                              <div className="flex items-center space-x-2">
                                <AppIcon path={type.icon} size="sm" />
                                <span>{t(`data_types.${type.value}`)}</span>
                              </div>
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                    {property?.data_type !== field.value && (
                      <FormDescription className="text-amber-600">
                        ⚠️ {t('edit.data_type_change_warning')}
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Target Entity Types */}
              <FormField
                control={form.control}
                name="target_entity_types"
                render={() => (
                  <FormItem>
                    <FormLabel>
                      {t('edit.target_entity_types_label')} <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormDescription>
                      {t('edit.target_entity_types_hint')}
                    </FormDescription>
                    <div className="space-y-2">
                      {ENTITY_TYPE_VALUES.map((entityValue) => (
                        <FormField
                          key={entityValue}
                          control={form.control}
                          name="target_entity_types"
                          render={({ field: checkboxField }) => (
                            <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                              <FormControl>
                                <Checkbox
                                  checked={checkboxField.value?.includes(entityValue)}
                                  onCheckedChange={(checked) => {
                                    return checked
                                      ? checkboxField.onChange([...checkboxField.value, entityValue])
                                      : checkboxField.onChange(
                                        checkboxField.value?.filter(
                                          (value: string) => value !== entityValue
                                        )
                                      )
                                  }}
                                  data-testid={`entity-type-${entityValue}`}
                                />
                              </FormControl>
                              <FormLabel className="text-sm font-normal">
                                {t(`entity_types.${entityValue}`)}
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Enabled Checkbox */}
              <FormField
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="enabled-checkbox"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-normal">
                        {t('edit.enabled_label')}
                      </FormLabel>
                      <FormDescription>
                        {t('edit.enabled_hint')}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {/* Derived Flag Checkbox */}
              <FormField
                control={form.control}
                name="derived"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => {
                          field.onChange(checked)
                          // When toggling derived ON, suggest system_managed
                          if (checked === true && !form.getValues('system_managed')) {
                            form.setValue('system_managed', true)
                          }
                        }}
                        data-testid="derived-flag-checkbox"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-normal">
                        {t('edit.derived_label')}
                      </FormLabel>
                      <FormDescription>
                        {t('edit.derived_hint')}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {/* Derived info callout, with the derivations computing this
                  property or the buttons starting one (spec 2026-10-06-17). */}
              {(form.watch('derived') || property?.code) && (
                <div
                  className={form.watch('derived')
                    ? 'rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800 space-y-2'
                    : 'rounded-md border border-border p-4 text-sm space-y-2'}
                  data-testid="property-derivations-callout"
                >
                  {form.watch('derived') && <p>{t('edit.derived_info')}</p>}
                  {property?.code && (
                    <PropertyDerivations org={org || 'demo'} property={{ code: property.code, name: property.name as any }} />
                  )}
                </div>
              )}

              {/* Warning: derived but not system_managed */}
              {form.watch('derived') && !form.watch('system_managed') && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  {t('edit.derived_not_system_managed_warning')}
                </div>
              )}

              {/* System Managed Checkbox */}
              <FormField
                control={form.control}
                name="system_managed"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="system-managed-checkbox"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-normal">
                        {t('edit.system_managed_label')}
                      </FormLabel>
                      <FormDescription>
                        {t('edit.system_managed_hint')}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {/* Hidden Flag Checkbox */}
              <FormField
                control={form.control}
                name="hidden"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        data-testid="hidden-flag-checkbox"
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="text-sm font-normal">
                        {t('edit.hidden_label')}
                      </FormLabel>
                      <FormDescription>
                        {t('edit.hidden_hint')}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {/* With Time Checkbox - Only show for date type */}
              {form.watch('data_type') === 'date' && (
                <FormField
                  control={form.control}
                  name="with_time"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="with-time-checkbox"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-normal">
                          {t('edit.with_time_label')}
                        </FormLabel>
                        <FormDescription>
                          {t('edit.with_time_hint')}
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              )}

              {/* Regex Pattern Field - Only show for text type */}
              {form.watch('data_type') === 'text' && (
                <FormField
                  control={form.control}
                  name="regex_pattern"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('edit.regex_label')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t('edit.regex_placeholder')}
                          className="font-mono"
                          data-testid="regex-pattern-input"
                        />
                      </FormControl>
                      <FormDescription>
                        {t('edit.regex_hint')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Link Pattern Field - Only show for text type */}
              {form.watch('data_type') === 'text' && (
                <FormField
                  control={form.control}
                  name="link_pattern"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('edit.link_pattern_label')}</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder={t('edit.link_pattern_placeholder')}
                          className="font-mono"
                          data-testid="link-pattern-input"
                        />
                      </FormControl>
                      <FormDescription>
                        {t('edit.link_pattern_hint')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Value suggestions - Only show for text type (spec 2026-09-02-03) */}
              {form.watch('data_type') === 'text' && (
                <div className="space-y-4 rounded-lg border border-border p-4" data-testid="suggestions-card">
                  <h4 className="text-sm font-medium">{t('settings.suggestions.title')}</h4>
                  <FormField
                    control={form.control}
                    name="suggestions_enabled"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="suggestions-enabled-checkbox"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-normal">
                            {t('settings.suggestions.enable')}
                          </FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />

                  {form.watch('suggestions_enabled') && (
                    <>
                      <FormField
                        control={form.control}
                        name="suggestions_scope"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings.suggestions.scope')}</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="suggestions-scope-select">
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="asset_type">{t('settings.suggestions.scope_asset_type')}</SelectItem>
                                <SelectItem value="organization">{t('settings.suggestions.scope_organization')}</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="suggestions_depends_on"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('settings.suggestions.depends_on')}</FormLabel>
                            <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border border-border p-3">
                              {dependencyCandidates.length === 0 ? (
                                <p className="text-sm text-muted-foreground">{t('settings.suggestions.none')}</p>
                              ) : dependencyCandidates.map(candidate => (
                                <label key={candidate.code} className="flex items-center space-x-2 text-sm">
                                  <Checkbox
                                    checked={field.value.includes(candidate.code)}
                                    onCheckedChange={(checked) => {
                                      field.onChange(checked
                                        ? [...field.value, candidate.code]
                                        : field.value.filter((c: string) => c !== candidate.code))
                                    }}
                                    data-testid={`suggestions-depends-on-${candidate.code}`}
                                  />
                                  <span>{candidate.label}</span>
                                  <code className="text-xs text-muted-foreground">{candidate.code}</code>
                                </label>
                              ))}
                            </div>
                            <FormDescription>
                              {t('settings.suggestions.depends_on_help')}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}
                </div>
              )}

              {/* Number Constraints - Only show for number type */}
              {form.watch('data_type') === 'number' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="min_value"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('edit.min_value_label')}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              step="any"
                              placeholder={t('edit.min_value_placeholder')}
                              data-testid="min-value-input"
                            />
                          </FormControl>
                          <FormDescription>
                            {t('edit.min_value_hint')}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="max_value"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('edit.max_value_label')}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="number"
                              step="any"
                              placeholder={t('edit.max_value_placeholder')}
                              data-testid="max-value-input"
                            />
                          </FormControl>
                          <FormDescription>
                            {t('edit.max_value_hint')}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="integer_only"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="integer-only-checkbox"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm font-normal">
                            {t('edit.integer_only_label')}
                          </FormLabel>
                          <FormDescription>
                            {t('edit.integer_only_hint')}
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
              )}

              {/* Boolean Semantic Field - Only show for boolean type */}
              {form.watch('data_type') === 'boolean' && (
                <FormField
                  control={form.control}
                  name="semantic"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('edit.boolean_semantic_label')}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="boolean-semantic">
                            <SelectValue placeholder={t('edit.boolean_semantic_placeholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="neutral">{t('edit.semantic_neutral')}</SelectItem>
                          <SelectItem value="positive">{t('edit.semantic_positive')}</SelectItem>
                          <SelectItem value="negative">{t('edit.semantic_negative')}</SelectItem>
                          <SelectItem value="personal">{t('edit.semantic_personal')}</SelectItem>
                          <SelectItem value="warning">{t('edit.semantic_warning')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {t('edit.boolean_semantic_hint')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* JSON Schema Field - Only show for json type */}
              {form.watch('data_type') === 'json' && (
                <FormField
                  control={form.control}
                  name="json_schema"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('edit.json_schema_label')}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder='{"type": "object", "properties": {"name": {"type": "string"}}}'
                          className="font-mono min-h-[120px]"
                          data-testid="json-schema-input"
                        />
                      </FormControl>
                      <FormDescription>
                        {t('edit.json_schema_hint')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Multiple Selection Checkbox - Only show for enum type */}
              {form.watch('data_type') === 'enum' && (
                <FormField
                  control={form.control}
                  name="multiple"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="multiple-selection-checkbox"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-normal">
                          {t('edit.multiple_selection_label')}
                        </FormLabel>
                        <FormDescription>
                          {t('edit.multiple_selection_hint')}
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              )}

              {/* Attribute Flag Checkbox - Show for boolean and enum types */}
              {(form.watch('data_type') === 'boolean' || form.watch('data_type') === 'enum') && (
                <FormField
                  control={form.control}
                  name="attribute"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="attribute-flag-checkbox"
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-sm font-normal">
                          {t('edit.attribute_label')}
                        </FormLabel>
                        <FormDescription>
                          {t('edit.attribute_hint')}
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              )}

              {/* Form Actions */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-border">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  data-testid="cancel-button"
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={!isFormValid || !hasChanges || updateProperty.isPending}
                  data-testid="update-button"
                >
                  {updateProperty.isPending ? t('edit.updating') : t('edit.update_button')}
                </Button>
              </div>
            </form>
          </Form>
        </MultilingualFormProvider>

        {/* Select Values Management Section */}
        {form.watch('data_type') === 'enum' && (
          <>
            <Separator />
            <div className="p-6">
              <SelectValuesManager org={org || 'demo'} propertyUid={propertyUid || ''} />
            </div>
          </>
        )}

        {/* Usage Information Section */}
        <Separator />
        <div className="p-6">
          <h3 className="text-lg font-medium mb-2">{t('edit.usage_title')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('edit.usage_subtitle')}
          </p>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">{t('edit.created_label')}</span>{' '}
              {property.created_at ? new Date(property.created_at).toLocaleDateString() : t('edit.unknown')}
            </div>
            <div>
              <span className="font-medium">{t('edit.updated_label')}</span>{' '}
              {property.updated_at ? new Date(property.updated_at).toLocaleDateString() : t('edit.unknown')}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}